import React, { useState } from "react";
import MtaStsValidationResultTable, { IMtaStsValidationTableItem } from "./MtaStsValidationResultTable";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { IRcaMtaStsValidationTxtRecordDetails } from "../../models/IRcaMtaStsValidationTxtRecordDetails";
import { IRcaMtaStsSectionDetails } from "../../models/IRcaMtaStsSectionDetails";
import { CollapsePanel } from "../CollapsePanel";
import { mergeStyleSets, Stack, StackItem } from "@fluentui/react";
import { MtaStsValidationResultIcon } from "./MtaStsValidationResultIcon";
import { ComponentDecorators } from "../ComponentDecorators";

const txtClassNames = mergeStyleSets({
  root: {
    paddingLeft: 30,
    margin: 15,
  },
});

/**
 * Gets the display for the TXT record collapsible header.
 *
 * @param txtRecord the TXT record properties
 * @returns the display for TXT record collapsible header
 */
const getTxtRecordDisplay = (txtRecord: IRcaMtaStsSectionDetails<IRcaMtaStsValidationTxtRecordDetails>): React.ReactElement => {
  const shownTxtRecordValue: string = txtRecord.Value?.RawTxtRecord || "n/a";
  return (
    <Stack horizontal verticalAlign="center" horizontalAlign="space-around" tokens={{ childrenGap: 8 }}>
      <StackItem>{WebsiteStrings.MtaStsTxtRecordLabel}</StackItem>
      <StackItem>{shownTxtRecordValue}</StackItem>
      <MtaStsValidationResultIcon mtaStsDetails={txtRecord.Details} mtaStsStatus={txtRecord.Status} />
    </Stack>
  );
};

/**
 * The function to map the received TXT record into table items.
 *
 * @param txtRecord  The received TXT record to map.
 * @returns The populated table items.
 */
const mapTxtRecordToMtaStsValidationTableItem = (txtRecord: IRcaMtaStsValidationTxtRecordDetails): IMtaStsValidationTableItem[] => {
  return [
    {
      Component: "Record",
      Value: txtRecord?.Record?.Value ?? "n/a",
      Status: txtRecord?.Record?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(txtRecord?.Record?.Details ?? "n/a"),
    },
    {
      Component: "Version",
      Value: txtRecord?.Version?.Value ?? "n/a",
      Status: txtRecord?.Version?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(txtRecord?.Version?.Details ?? "n/a"),
    },
    {
      Component: "ID",
      Value: txtRecord?.Id?.Value ?? "n/a",
      Status: txtRecord?.Id?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(txtRecord?.Id?.Details ?? "n/a"),
    },
    {
      Component: "TTL",
      Value: txtRecord?.Ttl?.Value ?? "n/a",
      Status: txtRecord?.Ttl?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
      Details: ComponentDecorators.replaceLinks(txtRecord?.Ttl?.Details ?? "n/a"),
    },
  ];
};

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
const MtaStsValidationResultTxtRecord: React.FC<{
  /**
   * List of items to render in the table
   */
  txtRecord: IRcaMtaStsSectionDetails<IRcaMtaStsValidationTxtRecordDetails>;
}> = ({ txtRecord }) => {
  const [expanded, setExpanded] = useState(true);

  const tableItems = mapTxtRecordToMtaStsValidationTableItem(txtRecord.Value);

  return (
    <Stack className={txtClassNames.root}>
      <CollapsePanel
        expanded={expanded}
        renderTitleRow={(): React.ReactElement => getTxtRecordDisplay(txtRecord)}
        setExpanded={setExpanded}
        styles={{
          content: {
            root: {
              marginLeft: 20,
            },
          },
        }}
      >
        <MtaStsValidationResultTable items={tableItems} />
      </CollapsePanel>
    </Stack>
  );
};

export default MtaStsValidationResultTxtRecord;
